import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import {
  Box, Typography, Table, TableBody, TableCell, TableHead, TableRow,
  Chip, Button, TextField, Select, MenuItem, FormControl, InputLabel,
  Alert, CircularProgress, Stack, Paper, IconButton, Tooltip,
  Dialog, DialogTitle, DialogContent, DialogActions, Divider,
  InputAdornment, TablePagination, Collapse, TableContainer,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  Refresh as RefreshIcon,
  CheckCircle as VerifiedIcon,
  Cancel as UnverifiedIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  QrCode2 as BarcodeIcon,
} from '@mui/icons-material';
import { adminApi } from '../services/adminApi';

const API = `${process.env.REACT_APP_EXPRESS_API_URL || 'https://famelia-admin-api-web.azurewebsites.net'}/api/admin`;

interface Nutrient {
  id?: string;
  nutrient_name: string;
  nutrient_value: number;
  unit: string;
  daily_value_pct?: number | null;
}

interface CustomFood {
  id: string;
  barcode: string | null;
  name: string;
  brand: string | null;
  serving_size: string | null;
  servings_per_container: string | null;
  serving_weight_grams: number | null;
  verified: boolean;
  rejected: boolean;
  verification_count: number;
  label_image_url: string | null;
  created_by: string | null;
  created_at: string;
  updated_at: string;
  nutrients: Nutrient[];
}

const CORE_NUTRIENTS = [
  { name: 'Calories', unit: 'kcal' },
  { name: 'Total Fat', unit: 'g' },
  { name: 'Saturated Fat', unit: 'g' },
  { name: 'Trans Fat', unit: 'g' },
  { name: 'Cholesterol', unit: 'mg' },
  { name: 'Sodium', unit: 'mg' },
  { name: 'Total Carbohydrate', unit: 'g' },
  { name: 'Dietary Fiber', unit: 'g' },
  { name: 'Total Sugars', unit: 'g' },
  { name: 'Protein', unit: 'g' },
  { name: 'Vitamin D', unit: 'mcg' },
  { name: 'Calcium', unit: 'mg' },
  { name: 'Iron', unit: 'mg' },
  { name: 'Potassium', unit: 'mg' },
];

const emptyForm = (): Omit<CustomFood, 'id' | 'created_at' | 'updated_at' | 'created_by' | 'label_image_url' | 'verification_count'> => ({
  barcode: '',
  name: '',
  brand: '',
  serving_size: '',
  servings_per_container: '',
  serving_weight_grams: null,
  verified: false,
  rejected: false,
  nutrients: CORE_NUTRIENTS.map(n => ({ nutrient_name: n.name, nutrient_value: 0, unit: n.unit })),
});

export function CustomBarcodesTab() {
  const [foods, setFoods] = useState<CustomFood[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [verifiedFilter, setVerifiedFilter] = useState<string>('all');
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogMode, setDialogMode] = useState<'create' | 'edit'>('create');
  const [editingFood, setEditingFood] = useState<any>(emptyForm());
  const [saving, setSaving] = useState(false);
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);

  const authHeader = { Authorization: adminApi.getAuthToken() };

  const fetchFoods = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params: any = {
        limit: rowsPerPage,
        offset: page * rowsPerPage,
        sort_by: 'created_at',
        sort_dir: 'desc',
      };
      if (search.trim()) params.search = search.trim();
      if (verifiedFilter !== 'all') params.verified = verifiedFilter;
      const res = await axios.get(`${API}/custom-foods`, { headers: authHeader, params });
      setFoods(res.data.data ?? []);
      setTotal(res.data.total ?? 0);
    } catch (err: any) {
      setError(err.response?.data?.error ?? err.message ?? 'Failed to load custom foods.');
    } finally {
      setLoading(false);
    }
  }, [search, verifiedFilter, page, rowsPerPage]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { fetchFoods(); }, [fetchFoods]);

  const handleOpenCreate = () => {
    setDialogMode('create');
    setEditingFood(emptyForm());
    setDialogOpen(true);
  };

  const handleOpenEdit = (food: CustomFood) => {
    setDialogMode('edit');
    const nutrients = CORE_NUTRIENTS.map(cn => {
      const existing = food.nutrients.find(n =>
        n.nutrient_name.toLowerCase() === cn.name.toLowerCase()
      );
      return existing
        ? { nutrient_name: cn.name, nutrient_value: existing.nutrient_value, unit: existing.unit, daily_value_pct: existing.daily_value_pct }
        : { nutrient_name: cn.name, nutrient_value: 0, unit: cn.unit };
    });
    const extras = food.nutrients.filter(n =>
      !CORE_NUTRIENTS.some(cn => cn.name.toLowerCase() === n.nutrient_name.toLowerCase())
    );
    setEditingFood({
      id: food.id,
      barcode: food.barcode || '',
      name: food.name,
      brand: food.brand || '',
      serving_size: food.serving_size || '',
      servings_per_container: food.servings_per_container || '',
      serving_weight_grams: food.serving_weight_grams,
      verified: food.verified,
      nutrients: [...nutrients, ...extras],
    });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!editingFood.name?.trim()) {
      setError('Name is required.');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const payload = {
        name: editingFood.name.trim(),
        brand: editingFood.brand?.trim() || null,
        barcode: editingFood.barcode?.trim() || null,
        serving_size: editingFood.serving_size?.trim() || null,
        servings_per_container: editingFood.servings_per_container?.trim() || null,
        serving_weight_grams: editingFood.serving_weight_grams || null,
        verified: editingFood.verified,
        nutrients: editingFood.nutrients.filter((n: Nutrient) => n.nutrient_value > 0),
      };
      if (dialogMode === 'create') {
        await axios.post(`${API}/custom-foods`, payload, { headers: authHeader });
        setSuccess('Custom food created.');
      } else {
        await axios.patch(`${API}/custom-foods/${editingFood.id}`, payload, { headers: authHeader });
        setSuccess('Custom food updated.');
      }
      setDialogOpen(false);
      fetchFoods();
    } catch (err: any) {
      setError(err.response?.data?.error ?? err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: string) => {
    try {
      await axios.delete(`${API}/custom-foods/${id}`, { headers: authHeader });
      setSuccess('Custom food deleted.');
      setDeleteConfirmId(null);
      fetchFoods();
    } catch (err: any) {
      setError(err.response?.data?.error ?? err.message);
    }
  };

  const handleNutrientChange = (index: number, field: string, value: any) => {
    const updated = [...editingFood.nutrients];
    updated[index] = { ...updated[index], [field]: value };
    setEditingFood({ ...editingFood, nutrients: updated });
  };

  const addNutrientRow = () => {
    setEditingFood({
      ...editingFood,
      nutrients: [...editingFood.nutrients, { nutrient_name: '', nutrient_value: 0, unit: 'mg' }],
    });
  };

  const removeNutrientRow = (index: number) => {
    const updated = editingFood.nutrients.filter((_: any, i: number) => i !== index);
    setEditingFood({ ...editingFood, nutrients: updated });
  };

  const handleVerify = async (food: CustomFood) => {
    try {
      await axios.patch(`${API}/custom-foods/${food.id}`, { verified: true, rejected: false }, { headers: authHeader });
      setSuccess(`"${food.name}" marked as verified.`);
      fetchFoods();
    } catch (err: any) {
      setError(err.response?.data?.error ?? err.message);
    }
  };

  const handleReject = async (food: CustomFood) => {
    try {
      await axios.patch(`${API}/custom-foods/${food.id}`, { rejected: true, verified: false }, { headers: authHeader });
      setSuccess(`"${food.name}" rejected — hidden from barcode lookups.`);
      fetchFoods();
    } catch (err: any) {
      setError(err.response?.data?.error ?? err.message);
    }
  };

  const getNutrientValue = (food: CustomFood, name: string): string => {
    const n = food.nutrients.find(nu => nu.nutrient_name.toLowerCase() === name.toLowerCase());
    return n ? `${n.nutrient_value}${n.unit}` : '—';
  };

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <BarcodeIcon color="primary" />
          <Typography variant="h5" fontWeight={700}>Custom Barcodes</Typography>
          <Chip label={`${total} total`} size="small" sx={{ ml: 1 }} />
        </Stack>
        <Stack direction="row" spacing={1}>
          <Tooltip title="Refresh">
            <IconButton onClick={fetchFoods}><RefreshIcon /></IconButton>
          </Tooltip>
          <Button variant="contained" startIcon={<AddIcon />} onClick={handleOpenCreate} sx={{ borderRadius: 5, textTransform: 'none' }}>
            Add Food
          </Button>
        </Stack>
      </Stack>

      {error && <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>{error}</Alert>}
      {success && <Alert severity="success" onClose={() => setSuccess(null)} sx={{ mb: 2 }}>{success}</Alert>}

      <Stack direction="row" spacing={2} mb={2}>
        <TextField
          size="small"
          placeholder="Search by name, brand, or barcode..."
          value={search}
          onChange={e => { setSearch(e.target.value); setPage(0); }}
          InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
          sx={{ minWidth: 320 }}
        />
        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel>Verified</InputLabel>
          <Select value={verifiedFilter} label="Status" onChange={e => { setVerifiedFilter(e.target.value); setPage(0); }}>
            <MenuItem value="all">All</MenuItem>
            <MenuItem value="true">Verified</MenuItem>
            <MenuItem value="false">Unverified</MenuItem>
            <MenuItem value="rejected">Rejected</MenuItem>
          </Select>
        </FormControl>
      </Stack>

      {loading ? (
        <Box display="flex" justifyContent="center" py={6}><CircularProgress /></Box>
      ) : foods.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography color="text.secondary">No custom foods found.</Typography>
        </Paper>
      ) : (
        <>
          <TableContainer component={Paper} sx={{ borderRadius: 2 }}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                  <TableCell width={40} />
                  <TableCell><strong>Name</strong></TableCell>
                  <TableCell><strong>Brand</strong></TableCell>
                  <TableCell><strong>Barcode</strong></TableCell>
                  <TableCell><strong>Serving</strong></TableCell>
                  <TableCell align="center"><strong>Cal</strong></TableCell>
                  <TableCell align="center"><strong>P</strong></TableCell>
                  <TableCell align="center"><strong>C</strong></TableCell>
                  <TableCell align="center"><strong>F</strong></TableCell>
                  <TableCell align="center"><strong>Status</strong></TableCell>
                  <TableCell><strong>Created</strong></TableCell>
                  <TableCell align="right"><strong>Actions</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {foods.map(food => (
                  <React.Fragment key={food.id}>
                    <TableRow hover sx={{ '& > td': { borderBottom: expandedId === food.id ? 'none' : undefined } }}>
                      <TableCell>
                        <IconButton size="small" onClick={() => setExpandedId(expandedId === food.id ? null : food.id)}>
                          {expandedId === food.id ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                        </IconButton>
                      </TableCell>
                      <TableCell><Typography variant="body2" fontWeight={600}>{food.name}</Typography></TableCell>
                      <TableCell><Typography variant="body2" color="text.secondary">{food.brand || '—'}</Typography></TableCell>
                      <TableCell><Typography variant="body2" fontFamily="monospace" fontSize="0.8rem">{food.barcode || '—'}</Typography></TableCell>
                      <TableCell><Typography variant="body2">{food.serving_size || '—'}</Typography></TableCell>
                      <TableCell align="center">{getNutrientValue(food, 'Calories')}</TableCell>
                      <TableCell align="center">{getNutrientValue(food, 'Protein')}</TableCell>
                      <TableCell align="center">{getNutrientValue(food, 'Total Carbohydrate')}</TableCell>
                      <TableCell align="center">{getNutrientValue(food, 'Total Fat')}</TableCell>
                      <TableCell align="center">
                        {food.rejected
                          ? <Chip icon={<UnverifiedIcon />} label="Rejected" color="error" size="small" variant="outlined" />
                          : food.verified
                            ? <Chip icon={<VerifiedIcon />} label="Verified" color="success" size="small" variant="outlined" />
                            : <Chip icon={<UnverifiedIcon />} label={`${food.verification_count} scan${food.verification_count !== 1 ? 's' : ''}`} size="small" variant="outlined" />
                        }
                      </TableCell>
                      <TableCell><Typography variant="body2" fontSize="0.8rem">{new Date(food.created_at).toLocaleDateString()}</Typography></TableCell>
                      <TableCell align="right">
                        {!food.verified && !food.rejected && (
                          <Tooltip title="Approve & Verify"><IconButton size="small" color="success" onClick={() => handleVerify(food)}><VerifiedIcon fontSize="small" /></IconButton></Tooltip>
                        )}
                        {!food.rejected && (
                          <Tooltip title="Reject"><IconButton size="small" color="warning" onClick={() => handleReject(food)}><UnverifiedIcon fontSize="small" /></IconButton></Tooltip>
                        )}
                        {food.rejected && (
                          <Tooltip title="Restore"><IconButton size="small" color="success" onClick={() => handleVerify(food)}><VerifiedIcon fontSize="small" /></IconButton></Tooltip>
                        )}
                        <Tooltip title="Edit"><IconButton size="small" onClick={() => handleOpenEdit(food)}><EditIcon fontSize="small" /></IconButton></Tooltip>
                        <Tooltip title="Delete"><IconButton size="small" color="error" onClick={() => setDeleteConfirmId(food.id)}><DeleteIcon fontSize="small" /></IconButton></Tooltip>
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell colSpan={12} sx={{ py: 0, px: 0 }}>
                        <Collapse in={expandedId === food.id} timeout="auto" unmountOnExit>
                          <Box sx={{ p: 2, bgcolor: '#fafafa' }}>
                            <Typography variant="subtitle2" gutterBottom>All Nutrients ({food.nutrients.length})</Typography>
                            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 1 }}>
                              {food.nutrients.map((n, i) => (
                                <Paper key={i} variant="outlined" sx={{ px: 1.5, py: 0.75, display: 'flex', justifyContent: 'space-between' }}>
                                  <Typography variant="body2" color="text.secondary">{n.nutrient_name}</Typography>
                                  <Typography variant="body2" fontWeight={600}>{n.nutrient_value} {n.unit}</Typography>
                                </Paper>
                              ))}
                            </Box>
                            {food.created_by && (
                              <Typography variant="caption" color="text.secondary" mt={1} display="block">
                                Created by: {food.created_by}
                              </Typography>
                            )}
                          </Box>
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  </React.Fragment>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={total}
            page={page}
            onPageChange={(_, p) => setPage(p)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={e => { setRowsPerPage(parseInt(e.target.value)); setPage(0); }}
            rowsPerPageOptions={[10, 25, 50, 100]}
          />
        </>
      )}

      {/* Create / Edit Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>{dialogMode === 'create' ? 'Add Custom Food' : `Edit: ${editingFood.name}`}</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} mt={1}>
            <Stack direction="row" spacing={2}>
              <TextField label="Name *" fullWidth size="small" value={editingFood.name} onChange={e => setEditingFood({ ...editingFood, name: e.target.value })} />
              <TextField label="Brand" fullWidth size="small" value={editingFood.brand} onChange={e => setEditingFood({ ...editingFood, brand: e.target.value })} />
            </Stack>
            <Stack direction="row" spacing={2}>
              <TextField label="Barcode" fullWidth size="small" value={editingFood.barcode} onChange={e => setEditingFood({ ...editingFood, barcode: e.target.value })} />
              <TextField label="Serving Size" fullWidth size="small" value={editingFood.serving_size} onChange={e => setEditingFood({ ...editingFood, serving_size: e.target.value })} />
            </Stack>
            <Stack direction="row" spacing={2}>
              <TextField label="Servings Per Container" size="small" sx={{ flex: 1 }} value={editingFood.servings_per_container} onChange={e => setEditingFood({ ...editingFood, servings_per_container: e.target.value })} />
              <TextField label="Serving Weight (g)" size="small" type="number" sx={{ flex: 1 }} value={editingFood.serving_weight_grams ?? ''} onChange={e => setEditingFood({ ...editingFood, serving_weight_grams: e.target.value ? parseFloat(e.target.value) : null })} />
              <FormControl size="small" sx={{ flex: 1 }}>
                <InputLabel>Verified</InputLabel>
                <Select label="Verified" value={editingFood.verified ? 'true' : 'false'} onChange={e => setEditingFood({ ...editingFood, verified: e.target.value === 'true' })}>
                  <MenuItem value="true">Yes</MenuItem>
                  <MenuItem value="false">No</MenuItem>
                </Select>
              </FormControl>
            </Stack>

            <Divider />
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Typography variant="subtitle1" fontWeight={600}>Nutrients</Typography>
              <Button size="small" startIcon={<AddIcon />} onClick={addNutrientRow}>Add Nutrient</Button>
            </Stack>

            <Box sx={{ maxHeight: 400, overflowY: 'auto' }}>
              {editingFood.nutrients.map((n: Nutrient, i: number) => (
                <Stack key={i} direction="row" spacing={1} alignItems="center" mb={1}>
                  <TextField
                    size="small"
                    label="Nutrient"
                    value={n.nutrient_name}
                    onChange={e => handleNutrientChange(i, 'nutrient_name', e.target.value)}
                    sx={{ flex: 2 }}
                  />
                  <TextField
                    size="small"
                    label="Value"
                    type="number"
                    value={n.nutrient_value}
                    onChange={e => handleNutrientChange(i, 'nutrient_value', parseFloat(e.target.value) || 0)}
                    sx={{ flex: 1 }}
                  />
                  <TextField
                    size="small"
                    label="Unit"
                    value={n.unit}
                    onChange={e => handleNutrientChange(i, 'unit', e.target.value)}
                    sx={{ width: 80 }}
                  />
                  <Tooltip title="Remove">
                    <IconButton size="small" color="error" onClick={() => removeNutrientRow(i)}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Stack>
              ))}
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)} sx={{ textTransform: 'none' }}>Cancel</Button>
          <Button variant="contained" onClick={handleSave} disabled={saving} sx={{ textTransform: 'none' }}>
            {saving ? <CircularProgress size={20} /> : dialogMode === 'create' ? 'Create' : 'Save Changes'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation */}
      <Dialog open={!!deleteConfirmId} onClose={() => setDeleteConfirmId(null)}>
        <DialogTitle>Delete Custom Food?</DialogTitle>
        <DialogContent>
          <Typography>This will permanently delete this food and all its nutrient data. This cannot be undone.</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmId(null)} sx={{ textTransform: 'none' }}>Cancel</Button>
          <Button variant="contained" color="error" onClick={() => deleteConfirmId && handleDelete(deleteConfirmId)} sx={{ textTransform: 'none' }}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
